import React from 'react';

import { MobileInteractionFieldRenderer } from './MobileInteractionFieldRenderer';
import {
  InteractionSection,
  InteractionTitle,
  FormField,
  RequiredMark,
} from './MobileStatusUpdateDialog.styles';
import { InteractionFormData, ServiceObject } from '../../../../../../@types/service.type';
import { FormConditionCompareOption } from '../../../../../../@types/form-builder.type';

interface MobileInteractionFormProps {
  interaction: InteractionFormData;
  formValues: Record<string, any>;
  onFieldChange: (interactionId: number, fieldId: number, value: any) => void;
  onBlockBypass?: (interactionId: number, fieldId: number) => void;
  service?: ServiceObject;
}

const evaluateConditions = (
  conditions: any[],
  interactionId: number,
  allFields: any[],
  formValues: Record<string, any>
): boolean => {
  if (!conditions || conditions.length === 0) return true;

  return conditions.every((condition) => {
    const watchedField = allFields.find((f) => f.id === condition.id);
    if (!watchedField) return true;

    const fieldKey = `${interactionId}-${watchedField.id}`;
    const currentValue = formValues[fieldKey] ?? watchedField.value ?? '';
    const compareValue = condition.value;
    const compareOption = condition.compareOption || FormConditionCompareOption.Equal;

    return compareValuesByOption(compareOption, String(currentValue), String(compareValue));
  });
};

const compareValuesByOption = (compareOption: string, value1: string, value2: string): boolean => {
  switch (compareOption) {
    case FormConditionCompareOption.Equal:
      return value1?.toLowerCase() === value2?.toLowerCase();
    case FormConditionCompareOption.Wildcard: {
      const haystack = (value1 ?? '').toLowerCase();
      const pattern = (value2 ?? '').toLowerCase();
      if (!pattern.includes('%')) return haystack === pattern;
      const tokens = pattern.split('%');
      const startsWithWild = pattern.startsWith('%');
      const endsWithWild = pattern.endsWith('%');
      let pos = 0;
      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        if (token === '') continue;
        if (i === 0 && !startsWithWild) {
          if (!haystack.startsWith(token)) return false;
          pos = token.length;
        } else if (i === tokens.length - 1 && !endsWithWild) {
          return haystack.endsWith(token) && haystack.length - token.length >= pos;
        } else {
          const idx = haystack.indexOf(token, pos);
          if (idx === -1) return false;
          pos = idx + token.length;
        }
      }
      return true;
    }
    default:
      return false;
  }
};

export const MobileInteractionForm: React.FC<MobileInteractionFormProps> = ({
  interaction,
  formValues,
  onFieldChange,
  onBlockBypass,
  service,
}) => {
  const templateContent = interaction.templateContent;
  if (!templateContent?.containers) return null;

  const hasFields = templateContent.containers.some((c) => c.fields.length > 0);
  if (!hasFields) return null;

  const allFields = templateContent.containers.flatMap((c) => c.fields);

  return (
    <InteractionSection>
      <InteractionTitle>
        {templateContent.title || interaction.name}
        {interaction.templateIsMandatory && <RequiredMark>*</RequiredMark>}
      </InteractionTitle>
      {templateContent.containers.map((container) => {
        const conditionsMet = evaluateConditions(
          container.conditions,
          interaction.id,
          allFields,
          formValues
        );
        if (!conditionsMet) return null;

        return (
          <div key={container.id}>
            {container.fields.map((field) => (
              <FormField key={field.id}>
                <MobileInteractionFieldRenderer
                  interactionId={interaction.id}
                  field={field}
                  formValues={formValues}
                  onFieldChange={onFieldChange}
                  onBlockBypass={onBlockBypass}
                  service={service}
                />
              </FormField>
            ))}
          </div>
        );
      })}
    </InteractionSection>
  );
};
